import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Shield, FileCheck, Clock, Lock, ArrowRight, MapPin, Phone } from "lucide-react";
import Footer from "@/components/Footer";

const LandingPage = () => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <FileCheck className="w-6 h-6" />,
      title: "OCR Document Scanning",
      description: "Automatic data extraction from passport and Emirates ID"
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Legally Binding",
      description: "Digital signatures with full legal compliance"
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Instant Processing",
      description: "Generate guarantee letters in minutes"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Secure Storage",
      description: "All documents encrypted and safely stored"
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url('https://images.unsplash.com/photo-1600068485426-4d32cd411697?crop=entropy&cs=srgb&fm=jpg&q=85')` 
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 hero-overlay" />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-sm mb-8">
            <Shield className="w-4 h-4 text-yellow-500" />
            <span className="text-sm text-white/90">White Sky Travel and Tourism LLC</span>
          </div>
          
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-10">
            UAE Visa Guarantee Form
          </h1>
          
          <div className="flex justify-center">
            <Button 
              data-testid="start-application-btn"
              onClick={() => navigate('/form')}
              className="h-12 px-8 text-base bg-white text-slate-900 hover:bg-slate-100 rounded-sm shadow-lg btn-press"
            >
              Start Application
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our streamlined process ensures quick and secure generation of guarantee letters 
              for all UAE visa types.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white border border-slate-200 rounded-sm p-6 card-hover"
              >
                <div className="w-12 h-12 bg-slate-100 rounded-sm flex items-center justify-center mb-4 text-slate-700">
                  {feature.icon}
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Visa Types Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Supported Visa Types
            </h2>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              "30 Days Single Entry",
              "60 Days Single Entry",
              "30 Days Multiple Entry",
              "60 Days Multiple Entry",
              "48 Hours Transit Visa",
              "96 Hours Transit Visa",
              "Inside Country Visa",
              "Visa Renewal"
            ].map((type, index) => (
              <div 
                key={index}
                className="border border-slate-200 rounded-sm p-4 text-center hover:border-slate-400 transition-colors"
              >
                <span className="text-sm font-medium text-slate-700">{type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Image Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-white mb-6">
                Quick & Secure Process
              </h2>
              <div className="space-y-4">
                {[
                  "Upload passport and Emirates ID",
                  "Automatic data extraction via OCR",
                  "Select visa type and guarantor details",
                  "Accept declarations and sign digitally",
                  "Download PDF and share via WhatsApp"
                ].map((step, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <span className="w-8 h-8 bg-yellow-500 text-slate-900 rounded-sm flex items-center justify-center font-bold text-sm">
                      {index + 1}
                    </span>
                    <span className="text-white/90">{step}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1639034741369-1e0c771adaeb?crop=entropy&cs=srgb&fm=jpg&q=85&w=600"
                alt="Travel Documents"
                className="rounded-sm shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LandingPage;
